import React, { useState } from 'react';
import { useAuth, API } from './AuthContext';
import { Mail, Lock, RefreshCw, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { SecurityDateTime } from '../security/SecurityIntegration.jsx';
import '../security/SecurityStyles.css';

export default function ForgotPasswordPage({ onBack }) {
  const { login } = useAuth();
  const [step, setStep]           = useState('request');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [otpInfo, setOtpInfo]     = useState(null);
  const [enteredOtp, setEnteredOtp] = useState('');
  const [newPass, setNewPass]     = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass]   = useState(false);

  const requestOtp = async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json();
      if (data.success) { setOtpInfo(data); setStep('verify'); }
      else setError(data.message || 'Failed to generate OTP.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (!enteredOtp) { setError('Enter the OTP.'); return; }
    if (newPass.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPass !== confirmPass) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp: enteredOtp, newPassword: newPass, confirmPassword: confirmPass })
      });
      const data = await res.json();
      if (data.success) setStep('success');
      else setError(data.message || 'Reset failed.');
    } catch { setError('Connection error.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      <div style={{ position:'absolute', top:20, right:28 }}>
        <SecurityDateTime style={{ color:'var(--auth-text-muted)' }} />
      </div>
      <div className="auth-page-grid" />
      <div className="auth-shell" style={{ maxWidth: 440 }}>
        <div className="auth-logo-wrap">
          <div className="auth-logo-badge" style={{ width:52, height:52, background:'#f59e0b', boxShadow:'0 0 28px rgba(245,158,11,0.4)' }}>
            <Lock size={22} color="#fff" />
          </div>
          <h1 className="auth-title" style={{ fontSize: 20 }}>Password Recovery</h1>
          <p className="auth-subtitle">MSN-CLIQ ERP — Secure Reset</p>
        </div>

        <div className="auth-card">
          {step === 'request' && (
            <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
              <div style={{ background:'var(--auth-input-bg)', borderRadius:10, padding:'16px 18px', display:'flex', alignItems:'flex-start', gap:12, border:'1px solid var(--auth-input-border)' }}>
                <Mail size={16} color="var(--brand-blue)" style={{ flexShrink:0, marginTop:2 }} />
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--auth-text)', marginBottom:4 }}>Recovery Email</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--brand-blue)' }}>msivan@myaccounts.in</div>
                  <div style={{ fontSize:10, color:'var(--auth-text-muted)', marginTop:4, lineHeight:1.5 }}>A 6-digit OTP will be generated and sent to this address. If email is not configured, OTP will be shown on screen.</div>
                </div>
              </div>
              {error && <div className="auth-error-banner">{error}</div>}
              <button
                onClick={requestOtp}
                disabled={loading}
                className="auth-submit"
                style={{ background:'#f59e0b', color:'#0f172a', boxShadow:'0 4px 16px rgba(245,158,11,0.3)' }}
                data-testid="request-otp-btn"
              >
                {loading ? 'Generating OTP...' : <><RefreshCw size={14}/> Generate OTP</>}
              </button>
              <button
                onClick={onBack}
                className="auth-link"
                style={{ color:'var(--auth-text-muted)' }}
              >
                Back to Login
              </button>
            </div>
          )}

          {step === 'verify' && (
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {otpInfo?.otp && (
                <div style={{ background:'var(--auth-input-bg)', borderRadius:10, padding:'16px 18px', border:'1px solid rgba(245,158,11,0.3)' }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#f59e0b', textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:8 }}>
                    {otpInfo.emailSent ? `OTP sent to ${otpInfo.recoveryEmail}` : 'OTP (Email not configured — Admin View)'}
                  </div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:32, fontWeight:900, letterSpacing:12, color:'var(--brand-blue)', textAlign:'center', padding:'8px 0' }}>{otpInfo.otp}</div>
                  <div style={{ fontSize:9, color:'var(--auth-text-muted)', textAlign:'center', marginTop:4 }}>Valid for 15 minutes</div>
                </div>
              )}

              <div>
                <label className="auth-label">Enter OTP</label>
                <input
                  value={enteredOtp}
                  onChange={e=>setEnteredOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                  maxLength={6}
                  placeholder="6-digit OTP"
                  className={`auth-input mono ${error?'error':''}`}
                  style={{ fontSize:24, letterSpacing:8, textAlign:'center' }}
                  data-testid="otp-input"
                />
              </div>

              <div>
                <label className="auth-label">New Password</label>
                <div style={{ position:'relative' }}>
                  <input
                    type={showPass?'text':'password'}
                    value={newPass}
                    onChange={e=>setNewPass(e.target.value)}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                    className="auth-input"
                    style={{ paddingRight: 44 }}
                    data-testid="new-password"
                  />
                  <button type="button" onClick={()=>setShowPass(p=>!p)} className="auth-show-pass-btn">
                    {showPass?<EyeOff size={15}/>:<Eye size={15}/>}
                  </button>
                </div>
              </div>

              <div>
                <label className="auth-label">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPass}
                  onChange={e=>setConfirmPass(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  className="auth-input"
                  data-testid="confirm-new-password"
                />
              </div>

              {error && <div className="auth-error-banner">{error}</div>}

              <button
                onClick={resetPassword}
                disabled={loading}
                className="auth-submit"
                data-testid="reset-submit"
              >
                {loading ? 'Resetting...' : <><Lock size={14}/> Reset Password</>}
              </button>
            </div>
          )}

          {step === 'success' && (
            <div style={{ textAlign:'center', padding:'24px 0', display:'flex', flexDirection:'column', gap:16, alignItems:'center' }}>
              <CheckCircle2 size={52} color="#10b981" />
              <div style={{ color:'#10b981', fontWeight:800, fontSize:18 }}>Password Reset!</div>
              <div style={{ color:'var(--auth-text-muted)', fontSize:12, lineHeight:1.6 }}>Your password has been updated. You can now login with your new credentials.</div>
              <button onClick={onBack} className="auth-submit" style={{ maxWidth: 220 }}>Back to Login</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
